import { PreviewBlock } from 'components/editor/GridDndEditor/Block/Preview/PreviewBlock';
import type { BlocksFromSectionsByDocumentIdApiResponse, SectionByDocumentIdApiResponse } from 'services/documents/DocumentRepository';
import type { BlockContent, BlocksContentCollection } from 'components/editor/grid/reduxStore/editorSlice';
import type { BlockSettings, ImageBlockSettings, ShapeBlockSettings, TextBlockSettings } from 'services/entities/BlockTypes';
import { getEditorMaxHeight } from 'components/editor/GridDndEditor/gridHelper';
import { GridBlockType } from 'services/entities/BlockTypes';
import { getPageSizeForOrientation, gridPixelSize } from 'components/editor/shared/gridConfig';
import type { PageOrientation, PageSize } from 'components/editor/shared/gridConfig';

import type { CSSProperties } from 'react';
import { Suspense } from 'react';
import { usePreviewSignatures } from 'hooks/usePreviewSignatures';
import type { ImageDimension } from 'components/editor/GridDndEditor/Block/Image/GridImageBlock/ImageComponent';
import ImageComponent, { resolveObjectFit } from 'components/editor/GridDndEditor/Block/Image/GridImageBlock/ImageComponent';
import { PreviewTableBlock } from 'components/editor/GridDndEditor/Block/Preview/PreviewTableBlock';
import TipTapTextblockPreview from 'components/editor/GridDndEditor/TiptapEditor/components/TipTapTextblockPreview';
import { getSignaturesMaxHeight } from 'components/editor/SidePanel/Signatures/SignatureHelper';
import PreviewSignatures from './PreviewSignatures';
import { SectionTitleTypography } from './SectionTitleTypography';
import { PREVIEW_SECTION_CONTAINER_CLASS } from './useTrackActiveSectionInPreview';
import { previewSectionsTestIds } from './PreviewSections.test-ids';
import { PreviewVideo } from './previewVideo';
import { PreviewEmbed } from './PreviewEmbed';
import { usePreviewVariables } from 'providers/PreviewVariablesContext';
import Square from 'components/editor/GridDndEditor/Block/Shape/SquareShapeComponent';
import { DynamicIcon } from 'components/editor/GridDndEditor/Block/Shape/DynamicICon';
import { ShapeType } from 'components/editor/grid/reduxStore/blockStyleSettingsSlice';
import { DEFAULT_SHAPE_BACKGROUND_COLOR } from 'components/editor/shared/documentColorDefaults';
import Circle from 'components/editor/GridDndEditor/Block/Shape/CircleShapeComponent';
import Triangle from 'components/editor/GridDndEditor/Block/Shape/TriangleShapeComponent';
import { defaultBlockStyle } from 'components/editor/hooks/useBlockSettings';
import TextWrapContainer from 'components/editor/GridDndEditor/Block/Text/TextWrapContainer';
import { useMobileZoomStyles } from './useMobileZoomStyles';

export const getBlocksContentFromApiResponse = (
  gridBlocks: BlocksFromSectionsByDocumentIdApiResponse[]
): { gridBlocksContent: BlocksContentCollection; blockStyleMap: Map<string, BlockSettings> } => {
  const gridBlocksContent: BlocksContentCollection = {};
  const blockStyleMap: Map<string, BlockSettings> = new Map();
  for (const gridBlockElement of gridBlocks) {
    const blockContent: BlockContent = {
      content: gridBlockElement.htmlContent,
      contentObject: gridBlockElement.contentObject,
      type: gridBlockElement.type,
      blockConfig: {
        id: gridBlockElement.gridId,
        x: gridBlockElement.position.left_px,
        y: gridBlockElement.position.top_px,
        z: gridBlockElement.position.z_index,
        width: gridBlockElement.dimensions.width_px,
        height: gridBlockElement.dimensions.height_px,
      },
    };
    if (gridBlockElement.type === GridBlockType.TABLE) {
      blockContent.contentTable = gridBlockElement.content;
    }

    gridBlocksContent[gridBlockElement.gridId] = blockContent;

    blockStyleMap.set(gridBlockElement.gridId, {
      blockType: gridBlockElement.type,
      updatedAt: '',
      blockId: gridBlockElement.gridId,
      borderTop: gridBlockElement.blockSettings?.borderTop || 0,
      borderLeft: gridBlockElement.blockSettings?.borderLeft || 0,
      borderRight: gridBlockElement.blockSettings?.borderRight || 0,
      borderBottom: gridBlockElement.blockSettings?.borderBottom || 0,
      borderColor: gridBlockElement.blockSettings?.borderColor || '',
      borderRadius: gridBlockElement.blockSettings?.borderRadius || 0,
      backgroundColor: (gridBlockElement.blockSettings as TextBlockSettings)?.backgroundColor || '',
      updatedByUserId: gridBlockElement.blockSettings?.updatedByUserId || 0,
      opacity: (gridBlockElement.blockSettings as ImageBlockSettings)?.opacity || 100,
      imageAlt: (gridBlockElement.blockSettings as ImageBlockSettings)?.imageAlt || '',
      imageLink: (gridBlockElement.blockSettings as ImageBlockSettings)?.imageLink || '',
      objectFit: (gridBlockElement.blockSettings as ImageBlockSettings)?.objectFit || '',
      objectPosition: (gridBlockElement.blockSettings as ImageBlockSettings)?.objectPosition || '',
      paddingLeft: gridBlockElement.blockSettings?.paddingLeft || 0,
      paddingRight: gridBlockElement.blockSettings?.paddingRight || 0,
      paddingTop: gridBlockElement.blockSettings?.paddingTop || 0,
      paddingBottom: gridBlockElement.blockSettings?.paddingBottom || 0,
      top: (gridBlockElement.blockSettings as ImageBlockSettings)?.top,
      rotation: gridBlockElement.blockSettings?.rotation,
    });
  }
  return { gridBlocksContent, blockStyleMap };
};

const PreviewSections = ({
  documentStyle,
  contentId,
  totalType,
  sections,
  isDocumentLocked,
}: {
  documentStyle: Record<string, any>;
  contentId: string;
  totalType: string;
  sections: SectionByDocumentIdApiResponse[];
  isDocumentLocked: boolean;
}) => {
  const { sectionsSignatures } = usePreviewSignatures({ contentId });
  const { width: canvasWidth, height: canvasMinHeight } =
    (documentStyle.pageSize as PageSize | undefined) ??
    getPageSizeForOrientation(undefined, documentStyle.pageOrientation as PageOrientation | undefined);
  const { wrapperStyle, innerStyle, getScaledHeight } = useMobileZoomStyles(canvasWidth);

  return (
    <>
      {sections.map((section, sectionIndex) => {
        const { gridBlocksContent, blockStyleMap } = getBlocksContentFromApiResponse(section.blocks);
        const basePageHeight = Math.max(
          canvasMinHeight,
          getEditorMaxHeight(gridBlocksContent, getSignaturesMaxHeight(sectionsSignatures[section.id]))
        );
        const pageHeight = getScaledHeight(basePageHeight);

        return (
          <div
            key={`preview-section-container-${section.id}`}
            id={section.id}
            className={PREVIEW_SECTION_CONTAINER_CLASS}
            data-section-id={section.id}
          >
            <SectionTitleTypography sectionId={section.id} sectionTitle={section.title} />

            <div style={{ ...wrapperStyle, height: pageHeight }}>
              <div
                key={`preview-section-${section.id}`}
                data-testid={previewSectionsTestIds.gridPreview}
                className={`preview__editor__wrapper__grid section-${sectionIndex}`}
                style={
                  {
                    '--canvas-width': `${canvasWidth}px`,
                    '--canvas-min-height': `${canvasMinHeight}px`,
                    backgroundColor: documentStyle.backgroundColor,
                    height: basePageHeight,
                    ...innerStyle,
                  } as CSSProperties
                }
              >
                <div className="preview__grid__blocks">
                  {Object.values(gridBlocksContent).map((block) => {
                    if (block.type === GridBlockType.TEXT) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      if (!blockSettings) return;
                      const {
                        paddingLeft,
                        paddingTop,
                        paddingRight,
                        paddingBottom,
                        backgroundColor,
                        borderColor,
                        borderLeft,
                        borderRight,
                        borderTop,
                        borderBottom,
                        borderRadius,
                      } = blockSettings as unknown as TextBlockSettings;
                      const styles = {
                        borderTop: `solid ${borderColor} ${borderTop}px`,
                        borderBottom: `solid ${borderColor} ${borderBottom}px`,
                        borderLeft: `solid ${borderColor} ${borderLeft}px`,
                        borderRight: `solid ${borderColor} ${borderRight}px`,
                        borderRadius: `${borderRadius}px`,
                        backgroundColor: backgroundColor,
                        paddingLeft: `${paddingLeft}px`,
                        paddingTop: `${paddingTop}px`,
                        paddingRight: `${paddingRight}px`,
                        paddingBottom: `${paddingBottom}px`,
                        height: '100%',
                      };

                      return (
                        <PreviewBlock
                          key={`preview-section-block-${block.blockConfig.id}`}
                          gridBlock={block}
                          gridSystemInPx={gridPixelSize}
                          rotation={blockSettings.rotation}
                        >
                          <div
                            className={block.blockConfig.id}
                            data-testid={previewSectionsTestIds.block(block.blockConfig.id)}
                            style={{
                              width: '100%',
                              height: '100%',
                            }}
                          >
                            <TextWrapContainer
                              borderRadius={borderRadius}
                              blockContent={block}
                              borderWidth={{ borderTop, borderBottom, borderLeft, borderRight }}
                            >
                              <Suspense fallback={<p>Loading...</p>}>
                                <TipTapTextblockPreview
                                  key={block.blockConfig.id}
                                  blockId={block.blockConfig.id}
                                  documentContent={block.content}
                                  usePreviewVariables={usePreviewVariables}
                                  currencySettings={documentStyle.currencySettings}
                                  style={styles}
                                />
                              </Suspense>
                            </TextWrapContainer>
                          </div>
                        </PreviewBlock>
                      );
                    } else if (block.type === GridBlockType.IMAGE) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      if (!blockSettings) return;
                      const imageAlt = 'imageAlt' in blockSettings ? blockSettings.imageAlt : '';
                      const imageLink = 'imageLink' in blockSettings && blockSettings.imageLink ? 'https://' + blockSettings.imageLink : '';
                      const {
                        paddingLeft,
                        paddingTop,
                        paddingRight,
                        paddingBottom,
                        borderColor,
                        borderLeft,
                        borderRight,
                        borderTop,
                        borderBottom,
                        borderRadius,
                        opacity,
                        top: imageTop,
                        objectFit,
                        objectPosition,
                      } = blockSettings as unknown as ImageBlockSettings;
                      const resolvedObjectFit = resolveObjectFit(objectFit);

                      const imageDimension: ImageDimension = {
                        width: block.blockConfig.width,
                        height: block.blockConfig.height,
                      };
                      return (
                        <PreviewBlock
                          key={block.blockConfig.id}
                          gridBlock={block}
                          gridSystemInPx={gridPixelSize}
                          rotation={blockSettings.rotation}
                        >
                          <div style={{ position: 'relative', overflow: 'hidden', width: '100%', height: '100%' }}>
                            {imageLink ? (
                              <a href={imageLink} target="_blank" rel="noopener">
                                <ImageComponent
                                  style={{
                                    borderTop: `solid ${borderColor} ${borderTop}px`,
                                    borderBottom: `solid ${borderColor} ${borderBottom}px`,
                                    borderLeft: `solid ${borderColor} ${borderLeft}px`,
                                    borderRight: `solid ${borderColor} ${borderRight}px`,
                                    borderRadius: `${borderRadius}px`,
                                    paddingLeft: `${paddingLeft}px`,
                                    paddingTop: `${paddingTop}px`,
                                    paddingRight: `${paddingRight}px`,
                                    paddingBottom: `${paddingBottom}px`,
                                    position: 'absolute',
                                    top: imageTop ?? 0,
                                    objectFit: resolvedObjectFit,
                                    objectPosition,
                                  }}
                                  imageAlt={imageAlt}
                                  opacity={opacity || 0}
                                  currentBlockId={block.blockConfig.id}
                                  src={block.content}
                                  dimension={imageDimension}
                                />
                              </a>
                            ) : (
                              <ImageComponent
                                style={{
                                  borderTop: `solid ${borderColor} ${borderTop}px`,
                                  borderBottom: `solid ${borderColor} ${borderBottom}px`,
                                  borderLeft: `solid ${borderColor} ${borderLeft}px`,
                                  borderRight: `solid ${borderColor} ${borderRight}px`,
                                  borderRadius: `${borderRadius}px`,
                                  paddingLeft: `${paddingLeft}px`,
                                  paddingTop: `${paddingTop}px`,
                                  paddingRight: `${paddingRight}px`,
                                  paddingBottom: `${paddingBottom}px`,
                                  position: 'absolute',
                                  top: imageTop ?? 0,
                                  objectFit: resolvedObjectFit,
                                  objectPosition,
                                }}
                                imageAlt={imageAlt}
                                opacity={opacity || 0}
                                currentBlockId={block.blockConfig.id}
                                src={block.content}
                                dimension={imageDimension}
                              />
                            )}
                          </div>
                        </PreviewBlock>
                      );
                    } else if (block.type === GridBlockType.TABLE) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      return (
                        <PreviewTableBlock
                          currencySettings={documentStyle.currencySettings}
                          key={block.blockConfig.id}
                          block={block}
                          isDocumentLocked={isDocumentLocked}
                          section={section}
                          rotation={blockSettings?.rotation}
                        />
                      );
                    } else if (block.type === GridBlockType.VIDEO) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      return <PreviewVideo key={block.blockConfig.id} block={block} rotation={blockSettings?.rotation} />;
                    } else if (block.type === GridBlockType.EMBED) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      return <PreviewEmbed key={block.blockConfig.id} block={block} rotation={blockSettings?.rotation} />;
                    } else if (block.type === GridBlockType.SHAPE) {
                      const blockSettings = blockStyleMap.get(block.blockConfig.id);
                      if (!blockSettings) return;
                      const shapeType = block.content || ShapeType.SQUARE;
                      const {
                        borderColor,
                        borderLeft = 0,
                        borderRadius = 0,
                      } = (blockSettings as unknown as ShapeBlockSettings) || defaultBlockStyle;
                      const backgroundColor =
                        (blockSettings as unknown as ShapeBlockSettings)?.backgroundColor || DEFAULT_SHAPE_BACKGROUND_COLOR;

                      const { width, height } = block.blockConfig;
                      const iconObject = {
                        [ShapeType.SQUARE]: Square,
                        [ShapeType.CIRCLE]: Circle,
                        [ShapeType.TRIANGLE]: Triangle,
                      };

                      return (
                        <PreviewBlock
                          key={block.blockConfig.id}
                          gridBlock={block}
                          gridSystemInPx={gridPixelSize}
                          rotation={blockSettings.rotation}
                        >
                          <DynamicIcon
                            width={width}
                            height={height}
                            borderRadius={borderRadius}
                            lockAspectRatio={false}
                            icon={iconObject[shapeType] || Square}
                            fill={backgroundColor}
                            stroke={borderColor}
                            strokeWidth={borderLeft / 10}
                          />
                        </PreviewBlock>
                      );
                    }
                    return null;
                  })}
                </div>
                <div className="preview__grid__signatures">
                  <div className="preview__signatures__container">
                    <PreviewSignatures
                      contentId={contentId}
                      totalType={totalType}
                      sectionId={section.id}
                      sectionsSignatures={sectionsSignatures}
                      canvasWidth={canvasWidth}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default PreviewSections;
